import {
  chakra,
  forwardRef,
  useToken,
  type HTMLChakraProps,
  type TableCellProps,
} from '@chakra-ui/react-v2';
import { cx } from '@chakra-ui/utils';
import { useMemo } from 'react';

import { useTheme } from '@frontend/legacy-components';

import { useTableStyles } from '../TableStylesContext';
import { useTableContext } from '../utils/context';
import { useTrContext } from '../utils/rowContext';
import { getClassName } from './className';
import { getCellProps } from './utils';

const relaxedBorderRadiusStyles = {
  left: {
    borderTopLeftRadius: 'sm',
    borderBottomLeftRadius: 'sm',
  },
  right: {
    borderTopRightRadius: 'sm',
    borderBottomRightRadius: 'sm',
  },
};

const RELAXED_PADDING_BY_SIZE: Record<string, number> = {
  sm: 0.75,
  md: 1,
};

export interface TdProps extends TableCellProps {
  direction?: TableCellProps['flexDirection'];
  innerProps?: HTMLChakraProps<'div'>;
  innerRef?: React.RefObject<HTMLDivElement>;
  outerProps?: HTMLChakraProps<'td'>;
}

export const Td = forwardRef<TdProps, 'td'>(
  (
    {
      as = 'div',
      children,
      direction = 'column',
      innerProps = {},
      outerProps = {},
      innerRef,
      className,
      ...restProps
    },
    ref,
  ) => {
    const theme = useTheme();
    const { td: tdStyles, tdInner: tdInnerStyles } = useTableStyles() ?? {};

    const { zebra, density, size } = useTableContext() ?? {};
    const { index: rowIndex } = useTrContext() ?? {};
    const [innerTdProps, outerTdProps] = getCellProps(restProps);

    const isEvenRow =
      rowIndex != null && rowIndex >= 0 && (rowIndex + 1) % 2 === 0;
    const shouldZebra =
      zebra != null &&
      rowIndex != null &&
      rowIndex >= 0 &&
      (isEvenRow ? zebra === 'even' : zebra === 'odd');

    const tdCss = useMemo(
      () => ({
        ...tdStyles,
        ...(density === 'relaxed'
          ? {
              _first: {
                pl: RELAXED_PADDING_BY_SIZE[size],
                ...relaxedBorderRadiusStyles.left,
                ...(outerTdProps.width
                  ? {
                      width: `calc(${
                        theme.space[RELAXED_PADDING_BY_SIZE[size]]
                      } + ${
                        theme.space[outerTdProps.width] ??
                        typeof outerTdProps.width === 'string'
                          ? outerTdProps.width
                          : `${outerTdProps.width}px`
                      })`,
                    }
                  : null),
                ...(outerTdProps.w
                  ? {
                      w: `calc(${
                        theme.space[RELAXED_PADDING_BY_SIZE[size]]
                      } + ${
                        theme.space[outerTdProps.w] ??
                        typeof outerTdProps.w === 'string'
                          ? outerTdProps.w
                          : `${outerTdProps.w}px`
                      })`,
                    }
                  : null),
              },
              _last: {
                pr: RELAXED_PADDING_BY_SIZE[size],
                ...relaxedBorderRadiusStyles.right,
              },
            }
          : null),
      }),
      [
        tdStyles,
        density,
        size,
        outerTdProps.width,
        outerTdProps.w,
        theme.space,
      ],
    );

    const { className: innerClassName, ...collectedInnerTdProps } = {
      flexDirection: direction,
      ...{
        [direction === 'column' ? 'justifyContent' : 'alignItems']: 'center',
        ...(shouldZebra
          ? {
              backgroundColor: 'page.plateLight',
            }
          : null),
      },
      ...innerTdProps,
      ...innerProps,
    };

    const collectedOuterTdProps = {
      ...outerTdProps,
      ...outerProps,
    };

    const backgroundColor = useToken(
      'colors',
      collectedInnerTdProps.backgroundColor ??
        tdInnerStyles?.backgroundColor ??
        'page.surface',
    );

    return (
      <chakra.td
        ref={ref}
        __css={tdCss}
        className={cx(getClassName('td'), className)}
        {...collectedOuterTdProps}
      >
        <chakra.div
          as={as}
          className={cx(getClassName('td-inner'), innerClassName)}
          __css={tdInnerStyles}
          {...collectedInnerTdProps}
          sx={{
            '--cell-background': backgroundColor,
            ...collectedInnerTdProps.sx,
          }}
          ref={innerRef}
        >
          {children}
        </chakra.div>
      </chakra.td>
    );
  },
);
